/**
 * HomeSettingsService — SHARED storage for the i-SYNERGY Home's full style
 * configuration (fonts, sizes, colors, padding, text labels, the liquid-glass
 * drawer — everything on the Home Settings page EXCEPT the icon images, which
 * are large binaries stored by HomeIconsService in a document library).
 *
 * Same mechanism as GridSettingsService: one JSON row in a brand-new,
 * single-purpose SharePoint list (iSyn_HomeSettings) that this service alone
 * owns and auto-creates on first Save. The owner configures the Home once and
 * every user sees it.
 *
 * READS on the always-loaded Home use a SILENT token so they never trigger an
 * auth redirect; any failure just leaves the Home on its local/default look.
 * WRITES (owner clicking Save) use the interactive token.
 */

import { createSharePointClient } from "./spClient";
import { MFG_SITE_URL } from "./mfgSite";
import { getSharePointTokenSilent } from "../../shell/auth/tokens";
import type { IHomeSettings } from "../../modules/home/homeSettings";

// Same site the Production Grid uses (iSyn_GridSettings) — the one proven to
// sync across all users. NOT the env default (VITE_SHAREPOINT_SITE_URL → CRM).
const SITE_URL = MFG_SITE_URL.replace(/\/$/, "");
const SETTINGS_LIST = "iSyn_HomeSettings";
const SETTINGS_FIELD = "settingsJson";
const CACHE_KEY = "ish:home-settings-shared";

interface ISettingsRow {
  Id: number;
  settingsJson?: string | null;
}

/** Strip the (large) icon data URLs before storing settings in the list —
 * icons live in the SharePoint document library via HomeIconsService. */
function withoutIcons(settings: IHomeSettings): Omit<IHomeSettings, "icons"> & { icons: Record<string, string> } {
  return { ...settings, icons: {} };
}

class HomeSettingsService {
  private readonly sp = createSharePointClient(SITE_URL);
  private entityType: string | null = null;

  /** Last shared settings cached locally — instant first paint, no flash. */
  public getCachedShared(): Partial<IHomeSettings> {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) return JSON.parse(raw) as Partial<IHomeSettings>;
    } catch {
      /* ignore */
    }
    return {};
  }

  /**
   * Silent read of the shared settings for the always-loaded Home — never
   * redirects. Returns null if SharePoint is unavailable/unauthorized or the
   * list doesn't exist yet (nothing configured), so the caller keeps its
   * current settings.
   */
  public async getSharedSettings(): Promise<Partial<IHomeSettings> | null> {
    const token = await getSharePointTokenSilent();
    if (!token) return null;
    try {
      const url = `${SITE_URL}/_api/web/lists/getbytitle('${encodeURIComponent(SETTINGS_LIST)}')/items?$select=Id,${SETTINGS_FIELD}&$top=1`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json;odata=verbose" },
      });
      if (!res.ok) return null;
      const json = (await res.json()) as { d: { results: ISettingsRow[] } };
      const raw = json.d.results[0]?.settingsJson;
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<IHomeSettings>;
      delete (parsed as { icons?: unknown }).icons; // icons come from the doc library
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(parsed)); } catch { /* ignore */ }
      return parsed;
    } catch {
      return null;
    }
  }

  /** Save the full settings (minus icons) to the shared list (owner action). */
  public async saveSharedSettings(settings: IHomeSettings): Promise<void> {
    await this.ensureListExists();
    const entityType = await this.getEntityType();
    const existing = await this.sp.getListItems<ISettingsRow>(SETTINGS_LIST, { select: "Id", top: 1 });
    const body = {
      __metadata: { type: entityType },
      [SETTINGS_FIELD]: JSON.stringify(withoutIcons(settings)),
    };
    if (existing[0]) {
      await this.sp.updateItem(SETTINGS_LIST, existing[0].Id, body);
    } else {
      await this.sp.createItem(SETTINGS_LIST, body);
    }
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(withoutIcons(settings))); } catch { /* ignore */ }
  }

  private async ensureListExists(): Promise<void> {
    try {
      await this.sp.getListItems(SETTINGS_LIST, { select: "Id", top: 1 });
      return;
    } catch (err) {
      const msg = (err as Error).message ?? "";
      if (!/404|does not exist|Invalid List Name/i.test(msg)) throw err;
    }
    await this.sp.raw(`/_api/web/lists`, {
      method: "POST",
      body: JSON.stringify({
        __metadata: { type: "SP.List" },
        Title: SETTINGS_LIST,
        Description: "i-SYNERGY Home — shared style settings (single JSON row). Created automatically on first Save.",
        BaseTemplate: 100,
      }),
    });
    await this.sp.raw(`/_api/web/lists/getbytitle('${encodeURIComponent(SETTINGS_LIST)}')/fields`, {
      method: "POST",
      body: JSON.stringify({
        __metadata: { type: "SP.Field" },
        Title: SETTINGS_FIELD,
        FieldTypeKind: 3, // Note — multi-line text, holds the settings JSON blob
        Required: false,
      }),
    });
  }

  private async getEntityType(): Promise<string> {
    if (this.entityType) return this.entityType;
    const res = await this.sp.raw<{ d: { ListItemEntityTypeFullName: string } }>(
      `/_api/web/lists/getbytitle('${encodeURIComponent(SETTINGS_LIST)}')?$select=ListItemEntityTypeFullName`,
    );
    this.entityType = res.d.ListItemEntityTypeFullName;
    return this.entityType;
  }
}

export const homeSettingsService = new HomeSettingsService();
